import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import ScheduledTasks from "@/pages/ScheduledTasks";
import Security from "@/pages/Security";
import DocumentArchive from "@/pages/DocumentArchive";
import DocNoSettings from "@/components/DocNoSettings";
import SecurityLedger from "@/pages/SecurityLedger";
import PlatformManagement from "@/components/PlatformManagement";
import MarketSettings from "@/components/MarketSettings";
import CostWidget from "@/components/CostWidget";
import BackgroundJobsPanel from "@/components/BackgroundJobsPanel";
import { isMarketFamilySector } from "@/lib/sectorVisibility";
import { CalendarClock, ShieldAlert, Archive, Fingerprint, ServerCog, Coins, Zap, CornerDownRight, ChevronRight, Activity } from "lucide-react";

export default function SystemMonitor({ initialSub = null }) {
  const { can, feature, activeSector } = useAuth();
  const [sub, setSub] = useState(initialSub);
  const [secView, setSecView] = useState("events");
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
  const [jobsRunning, setJobsRunning] = useState(false);
  useEffect(() => { api.get("/platform/status").then((r) => setIsPlatformAdmin(!!r.data.is_platform_admin)).catch(() => {}); }, []);
  useEffect(() => { if (initialSub) { setSub(initialSub); setSecView("events"); } }, [initialSub]);
  useEffect(() => {
    if (!can("catalog.read") && !can("config.manage")) return;
    const f = () => api.get("/jobs/overview").then((r) => setJobsRunning(!!(r.data?.market?.running || r.data?.ai_image?.running))).catch(() => {});
    f();
    const t = setInterval(f, 5000);
    return () => clearInterval(t);
  }, [can]);
  // Kur & Yakıt görünürlüğü tri-state: sektör varsayılanı (market ailesi) + Platform override (zorla aç/kapat).
  const secDef = isMarketFamilySector(activeSector);
  const showMarketPricing = feature("module.market_pricing", secDef);
  const showFuelPricing = feature("module.fuel_pricing", secDef);
  const canMarket = (can("fx.manage") && showMarketPricing) || (can("logistics.manage") && showFuelPricing);
  const canLedger = can("approve.admin");
  const canCost = can("cost.read");
  const SUB = [
    ...(isPlatformAdmin ? [["platform", "Platform Yönetimi", ServerCog]] : []),
    ["security", "Güvenlik & Görevler", ShieldAlert],
    ...(can("catalog.read") || can("config.manage") ? [["jobs", "Arka Plan İşleri", Activity]] : []),
    ["archive", "Belge Arşivi", Archive],
    ...(canMarket ? [["market", "Kur & Yakıt", Coins]] : []),
    ...(canCost ? [["cost", "Kullanım Maliyeti", Zap]] : []),
  ];
  const activeSub = sub || (SUB[0] ? SUB[0][0] : "tasks");
  const pick = (k) => { setSub(k); setSecView("events"); };
  const subLabel = (SUB.find(([k]) => k === activeSub) || [0, ""])[1];
  return (
    <div className="space-y-3" data-testid="system-monitor">
      {/* Breadcrumb: Sistem Ayarları › Sistem Yönetimi › {alt} › {alt-alt} */}
      <div className="flex flex-wrap items-center gap-1.5 text-[11px] font-medium text-slate-400" data-testid="monitor-breadcrumb">
        <span className="font-bold uppercase tracking-wide text-slate-500">Sistem Ayarları</span>
        <ChevronRight className="h-3 w-3" />
        <span className="font-bold text-brand-green">Sistem Yönetimi</span>
        <ChevronRight className="h-3 w-3" />
        <span className="font-semibold text-slate-600">{subLabel}</span>
        {activeSub === "security" && (
          <><ChevronRight className="h-3 w-3" /><span className="font-semibold text-slate-500">{secView === "ledger" ? "Değişiklik Defteri" : secView === "tasks" ? "Zamanlanmış Görevler" : "Olay Günlüğü"}</span></>
        )}
      </div>

      {/* Seviye 2: alt sekmeler (üst sekmeden görsel olarak farklı — beyaz + halka + üst etiket) */}
      <div className="flex flex-wrap items-center gap-2" data-testid="monitor-subtabs-wrap">
        <span className="flex items-center gap-1 rounded-md bg-brand-green/10 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-brand-green"><CornerDownRight className="h-3 w-3" /> Sistem Yönetimi</span>
        <div className="inline-flex flex-wrap rounded-lg bg-white p-1 ring-1 ring-inset ring-slate-200">
          {SUB.map(([k, l, Icon]) => (
            <button key={k} data-testid={`monitor-sub-${k}`} onClick={() => pick(k)}
              className={`flex items-center gap-1.5 rounded-md px-3.5 py-1.5 text-sm font-semibold transition-colors ${activeSub === k ? "bg-brand-green/10 text-brand-green ring-1 ring-inset ring-brand-green/30" : "text-slate-500 hover:text-slate-700"}`}>
              <Icon className="h-4 w-4" /> {l}
              {k === "jobs" && jobsRunning && <span data-testid="jobs-subtab-running-dot" title="Bir arka plan işi çalışıyor" className="h-2 w-2 animate-pulse rounded-full bg-blue-500" />}
            </button>
          ))}
        </div>
      </div>

      {activeSub === "jobs" && <BackgroundJobsPanel />}
      {activeSub === "platform" && <PlatformManagement />}
      {activeSub === "market" && <MarketSettings />}
      {activeSub === "cost" && <CostWidget />}

      {activeSub === "security" && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 pl-3" data-testid="security-subtabs-wrap">
            <CornerDownRight className="h-3.5 w-3.5 text-slate-300" />
            <div className="inline-flex flex-wrap rounded-md bg-slate-50 p-0.5 ring-1 ring-inset ring-slate-200" data-testid="security-subtabs">
              {[["events", "Olay Günlüğü", ShieldAlert], ...(canLedger ? [["ledger", "Değişiklik Defteri (Güvenlik Gözü)", Fingerprint]] : []), ["tasks", "Zamanlanmış Görevler", CalendarClock]].map(([k, l, Icon]) => (
                <button key={k} data-testid={`security-sub-${k}`} onClick={() => setSecView(k)}
                  className={`flex items-center gap-1.5 rounded-sm px-3 py-1 text-[13px] font-semibold transition-colors ${secView === k ? "bg-white text-brand-green shadow-sm" : "text-slate-400 hover:text-slate-600"}`}>
                  <Icon className="h-3.5 w-3.5" /> {l}
                </button>
              ))}
            </div>
          </div>
          {secView === "events" && <Security />}
          {canLedger && secView === "ledger" && <SecurityLedger />}
          {secView === "tasks" && <ScheduledTasks />}
        </div>
      )}

      {activeSub === "archive" && (
        <div className="space-y-4">
          <DocNoSettings />
          <DocumentArchive />
        </div>
      )}
    </div>
  );
}
